import {
  FunctionComponent,
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Typography } from 'antd';
import dayjs from 'dayjs';
import {
  App,
  Button,
  Checkbox,
  Flex,
  Modal,
  ModalHandle,
  TextComponent,
} from '@monorepo/react-components';
import { useResponsiveness } from '@monorepo/react-hooks';
import { UserApproval } from '@monorepo/types';
import { getProject } from '../../helpers/get-project';
import { useStore } from '../../helpers/use-store';

import './profile-approval-modal.scss';

const The5ersSvg = lazy(() => import('./the5ers.svg?react'));
const TtpSvg = lazy(() => import('./ttp.svg?react'));

export const ProfileApprovalModal: FunctionComponent = () => {
  const {
    dataStore: { userStore, applicationConfigStore },
  } = useStore();
  const { t } = useTranslation(['errors', 'profile']);

  const { message } = App.useApp();

  const { isMobile } = useResponsiveness();

  const [agreed, setAgreed] = useState(false);
  const [approved, setApproved] = useState(false);

  const modalRef = useRef<ModalHandle>(null);

  const { is5ers, isTtp } = getProject();

  const { Link } = Typography;

  useEffect(() => {
    const isImpersonated = userStore.isImpersonated;

    const hasPublicApproval =
      userStore.currentUser.approvals?.[UserApproval.PublicProfile];

    const shouldShowApproveModal = !hasPublicApproval && !isImpersonated;

    if (shouldShowApproveModal) {
      modalRef.current?.open();
    }
  }, [userStore]);

  const approve = useCallback(() => {
    if (!agreed) return;

    if (!approved) {
      setApproved(true);
      userStore
        .update({
          profile: {
            private: false,
          },
          approvals: {
            ...userStore.currentUser.approvals,
            [UserApproval.PublicProfile]: dayjs().toDate(),
          },
        })
        .then(() => {
          modalRef.current?.ok();
        })
        .catch(() => {
          setApproved(false);
          message.error(t('errors:error_occurred_while_approving'));
        });
    }
  }, [agreed, approved, message, userStore]);

  return (
    <Modal
      ref={modalRef}
      forceOk
      footer={
        <div className="profile-approval__footer">
          <Button
            className={'profile-approval__button'}
            disabled={!agreed || approved}
            onClick={approve}
            type={'primary'}
          >
            <TextComponent weight={600}>I Accept</TextComponent>
          </Button>
        </div>
      }
    >
      <Flex className={'profile-approval'} vertical align="center" gap={10}>
        {isTtp && (
          <Suspense>
            <TtpSvg
              className={'profile-approval__svg'}
              style={{ width: isMobile ? 100 : 160 }}
            />
          </Suspense>
        )}
        <TextComponent
          uppercase={true}
          level={1}
          weight={600}
          type="success"
          textAlign="center"
        >
          {t('profile:profile_approval_modal.new_profile_update')}
        </TextComponent>
        <TextComponent
          level={3}
          weight={600}
          textAlign="center"
          responsive={false}
        >
          {t(
            'profile:profile_approval_modal.we_are_improving_your_trader_experience',
          )}
        </TextComponent>
        <TextComponent
          textAlign={'center'}
          className="profile-approval__text"
          weight={300}
        >
          <Trans
            ns="profile"
            i18nKey="profile_approval_modal.profile_visibility_message"
            components={{
              Link: (
                <Link
                  underline
                  className="profile-approval__link"
                  href="/profile"
                />
              ),
            }}
          />
        </TextComponent>
        <Flex align="center" gap={8}>
          <Checkbox onChange={(e) => setAgreed(e.target.checked)} />
          <TextComponent weight={300} level={7}>
            <Trans
              ns="profile"
              i18nKey="profile_approval_modal.i_have_read_and_agree_to_the_new_privacy_policy"
              components={{
                Link: (
                  <Link
                    underline
                    className="profile-approval__link"
                    href={
                      applicationConfigStore.applicationConfig.privacyPolicy
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                  />
                ),
              }}
            />
          </TextComponent>
        </Flex>
        {is5ers && (
          <Suspense>
            <The5ersSvg
              className={'profile-approval__svg'}
              style={{ width: isMobile ? 300 : 400 }}
            />
          </Suspense>
        )}
      </Flex>
    </Modal>
  );
};
